/**
 * Object Types Configuration
 *
 * Centralized definitions for predefined object types used in mapping configuration.
 * These represent the different component types in the piping system.
 */

/**
 * Categories for grouping object types
 */
export enum ObjectTypeCategory {
  GENERAL = 'General',
  PIPING = 'Piping',
  COMPONENTS = 'Components',
  SUPPORT = 'Support',
  INSTRUMENTATION = 'Instrumentation',
}

/**
 * Interface for predefined object type definition
 */
export interface PredefinedObjectType {
  /** The object type name/value */
  name: string;
  /** Display label for the object type */
  label: string;
  /** Category grouping */
  category: ObjectTypeCategory;
  /** Description of what this object type represents */
  description?: string;
  /** Whether this is a system/required type that cannot be deleted */
  isSystem?: boolean;
}

/**
 * Predefined object types available for selection in the mapping configuration
 */
export const PREDEFINED_OBJECT_TYPES: PredefinedObjectType[] = [
  {
    name: 'ALL',
    label: 'ALL',
    category: ObjectTypeCategory.GENERAL,
    description: 'Applies to all object types universally',
    isSystem: true,
  },
  {
    name: 'BOLT',
    label: 'BOLT',
    category: ObjectTypeCategory.COMPONENTS,
    description: 'Bolt fasteners for flanged connections',
  },
  {
    name: 'MESSAGE',
    label: 'MESSAGE',
    category: ObjectTypeCategory.GENERAL,
    description: 'Message or annotation elements',
  },
  {
    name: 'VALVE',
    label: 'VALVE',
    category: ObjectTypeCategory.COMPONENTS,
    description: 'Valve components for controlling flow',
  },
  {
    name: 'SUPPORT',
    label: 'SUPPORT',
    category: ObjectTypeCategory.SUPPORT,
    description: 'Pipe supports and hangers (SUPP, ANCH, GUID, SPRG, etc.)',
  },
  {
    name: 'INSTRUMENT',
    label: 'INSTRUMENT',
    category: ObjectTypeCategory.INSTRUMENTATION,
    description: 'Instrumentation devices and sensors',
  },
  {
    name: 'END-CONNECTION-EQUIPMENT',
    label: 'END-CONNECTION-EQUIPMENT',
    category: ObjectTypeCategory.COMPONENTS,
    description: 'Equipment connection endpoints',
  },
  {
    name: 'PIPE',
    label: 'PIPE',
    category: ObjectTypeCategory.PIPING,
    description: 'Pipe segments and runs',
  },
  {
    name: 'CUSTOM',
    label: 'CUSTOM',
    category: ObjectTypeCategory.GENERAL,
    description: 'Custom user-defined object type',
  },
];

/**
 * Object type names as a simple string array for quick access
 */
export const PREDEFINED_OBJECT_TYPE_NAMES: string[] = PREDEFINED_OBJECT_TYPES.map((type) => type.name);

/**
 * Get object types filtered by category
 * @param category - The category to filter by
 * @returns Array of object types matching the category
 */
export function getObjectTypesByCategory(category: ObjectTypeCategory): PredefinedObjectType[] {
  return PREDEFINED_OBJECT_TYPES.filter((type) => type.category === category);
}

/**
 * Get a specific object type by its name
 * @param name - The object type name to search for
 * @returns The matching object type or undefined if not found
 */
export function getObjectTypeByName(name: string): PredefinedObjectType | undefined {
  return PREDEFINED_OBJECT_TYPES.find((type) => type.name === name);
}

/**
 * Check if an object type can be deleted (non-system types only)
 * @param name - The object type name to check
 * @returns True if the object type can be deleted
 */
export function canDeleteObjectType(name: string): boolean {
  const objectType = getObjectTypeByName(name);
  return objectType ? !objectType.isSystem : true;
}

/**
 * Get all unique categories from the object types
 * @returns Array of unique category values
 */
export function getAllObjectTypeCategories(): ObjectTypeCategory[] {
  return Object.values(ObjectTypeCategory);
}

/**
 * Get object types grouped by category
 * @returns Object with categories as keys and arrays of object types as values
 */
export function getObjectTypesGroupedByCategory(): Record<ObjectTypeCategory, PredefinedObjectType[]> {
  const grouped = {} as Record<ObjectTypeCategory, PredefinedObjectType[]>;

  for (const category of getAllObjectTypeCategories()) {
    grouped[category] = getObjectTypesByCategory(category);
  }

  return grouped;
}
